import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { UserAvatar } from "../../common/components/UserAvatar";

function SubMembersTab({ project }) {
  const { t } = useTranslation("members");

  const tree = useMemo(() => {
    const users = project?.users || [];
    const byId = {};
    users.forEach((u) => {
      byId[u.id] = { ...u, children: [] };
    });

    const roots = [];
    users.forEach((u) => {
      if (u.parent && byId[u.parent]) {
        byId[u.parent].children.push(byId[u.id]);
      } else {
        roots.push(byId[u.id]);
      }
    });

    // Flatten into rows with depth
    const rows = [];
    const walk = (node, depth) => {
      rows.push({ ...node, depth });
      node.children.forEach((child) => walk(child, depth + 1));
    };
    roots.forEach((root) => walk(root, 0));
    return rows;
  }, [project]);

  const getRoleColor = (role) => {
    switch (role?.toLowerCase()) {
      case "owner":
        return "primary";
      case "producer":
        return "secondary";
      case "co_producer":
        return "info";
      default:
        return "default";
    }
  };

  if (tree.length === 0) {
    return (
      <Typography color="text.secondary" sx={{ py: 3 }}>
        {t("subMembers.noSubMembers")}
      </Typography>
    );
  }

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>{t("subMembers.columns.member")}</TableCell>
            <TableCell>{t("subMembers.columns.parent")}</TableCell>
            <TableCell>{t("subMembers.columns.role")}</TableCell>
            <TableCell align="right">
              {t("subMembers.columns.revenueShare")}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {tree.map((row) => (
            <TableRow key={row.id}>
              <TableCell>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    pl: row.depth * 4,
                  }}
                >
                  <UserAvatar
                    name={row.user_details?.name}
                    image={row.user_details?.avatar}
                    size={32}
                  />
                  <Typography variant="bodyMd" sx={{ fontWeight: 500 }}>
                    {row.user_details?.name || t("table.unknownUser")}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell>
                <Typography variant="bodySm" color="text.secondary">
                  {row.parent_name || t("subMembers.rootMember")}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip
                  label={
                    row.role
                      ? t(`roles.${row.role.toLowerCase()}`)
                      : t("roles.member")
                  }
                  color={getRoleColor(row.role)}
                  size="small"
                  sx={{ fontWeight: 500, textTransform: "capitalize" }}
                />
              </TableCell>
              <TableCell align="right">
                {row.revenue_share_percentage ? (
                  <Typography variant="bodySm" sx={{ fontWeight: 500 }}>
                    {row.revenue_share_percentage}%
                  </Typography>
                ) : (
                  <Typography variant="bodySm" color="text.secondary">
                    -
                  </Typography>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default SubMembersTab;
